import { Checkbox } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import {
  AffixRequestApi,
  AffixRequestGridDto,
  AffixRequestSearch,
  ERequestState,
  ERequestType,
  RequestApi,
} from "../../../../api";
import { AddButton, EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { getRouteStr } from "../../../../utils/RequestRouteUtil";

export interface IAffixRequestListProps {}

export const AffixRequestList: React.FunctionComponent<IAffixRequestListProps> = (props) => {
  const api = useApiService(AffixRequestApi);
  const requestApi = useApiService(RequestApi);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();

  const redirectToRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType, editMode: string) => {
      const subRequestId = await requestApi.getSubRequestTypeId(requestId, requestType);
      const p = await getRouteStr(requestType, subRequestId, editMode);
      history.push(p);
    },
    [history, requestApi]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: AffixRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={async () => redirectToRequest(row.requestId, row.requestTypeCode, "view")}
            />

            <EditButton
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              minimal={true}
              onClick={async () => redirectToRequest(row.requestId, row.requestTypeCode, "edit")}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: AffixRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestType),
        fieldName: "requestType",
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: AffixRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [redirectToRequest, t]
  );

  const AddItemFunc = React.useCallback(() => {
    history.push(`${ERoutes.affixRequest}/0/detail/edit/new`);
  }, [history]);
  const search = React.useCallback(
    (sObj) => api.baseSearch(AffixRequestSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId })),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(AffixRequestSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId }))
      }
      sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.Requests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => AddItemFunc()} />
        </div>
      }
    />
  );
};
